import styled from 'styled-components';
import { Icon, type IconName } from '@/icons';
import {
  CATEGORY_LABELS,
  PLATFORM_LABELS,
  releaseDate,
  type ChangeGroup,
  type UpdateRelease,
} from '@/core/data/updateLogs';
import { Chip, Chips, Muted, Surface, Version, focusRing } from './styles';
import { PlatformIcon } from './PlatformIcon';

const Card = styled(Surface)<{ $open: boolean }>`
  scroll-margin-top: 98px;
  ${({ $open, theme }) => {
    if (!$open) return '';
    const tint = `${theme.color.brandFrom}1f`;
    const base = `${theme.surfaceGradients.panel}, ${theme.color.surface}`;
    return `background: linear-gradient(${tint}, ${tint}), ${base};`;
  }}
`;
const Header = styled.button`
  ${focusRing}
  display: flex;
  align-items: center;
  gap: 16px;
  width: 100%;
  min-height: 56px;
  padding: 10px 15px;
  color: inherit;
  background: transparent;
  border: 0;
  border-radius: ${({ theme }) => theme.radius.md};
  cursor: pointer;
  text-align: left;
  font: inherit;

  & > svg:last-child {
    margin-left: auto;
    flex-shrink: 0;
  }
  @media (max-width: 640px) {
    flex-wrap: wrap;
    gap: 8px 12px;
    padding: 13px;
    & > div {
      order: 3;
      flex-basis: 100%;
    }
  }
`;
const Badge = styled(Version)`
  flex-shrink: 0;
  min-width: 104px;
  padding: 5px 12px;
  text-align: center;
  border-radius: ${({ theme }) => theme.radius.sm};
  font-size: ${({ theme }) => theme.fontSize.sm};
  border: 1px solid transparent;
  background: ${({ theme }) => theme.color.brandFrom}1f;
`;
const Description = styled.span`
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: 3px;
  min-width: 0;
  & strong {
    font-size: ${({ theme }) => theme.fontSize.xs};
    line-height: 1.45;
    font-weight: 600;
  }
  & time {
    color: ${({ theme }) => theme.color.textMuted};
    font-size: 0.7rem;
  }
  @media (max-width: 640px) {
    flex-basis: calc(100% - 155px);
  }
`;
const Tags = styled(Chips)`
  justify-content: flex-end;
  max-width: 36%;
  gap: 6px;
  @media (max-width: 1100px) {
    max-width: 28%;
    & [data-category] {
      display: none;
    }
  }
  @media (max-width: 640px) {
    display: none;
  }
`;
const Body = styled.div`
  padding: 6px 22px 22px;
  border-top: 1px solid ${({ theme }) => theme.color.glassBorder};
  & > p {
    margin: 12px 0 18px;
  }
  @media (max-width: 640px) {
    padding: 4px 15px 18px;
  }
`;
const Columns = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 26px;
  & > div + div {
    border-left: 1px solid ${({ theme }) => theme.color.glassBorder};
    padding-left: 26px;
  }
  @media (max-width: 700px) {
    grid-template-columns: 1fr;
    gap: 20px;
    & > div + div {
      border-left: 0;
      border-top: 1px solid ${({ theme }) => theme.color.glassBorder};
      padding: 18px 0 0;
    }
  }
`;
const Group = styled.section<{ $tone: ChangeGroup }>`
  --group-tone: ${({ theme, $tone }) =>
    $tone === 'new'
      ? theme.color.brandFrom
      : $tone === 'fixes'
        ? theme.color.privacy
        : $tone === 'improvements'
          ? theme.color.success
          : theme.color.review};
  & + & {
    margin-top: 22px;
  }
  & h3 {
    display: flex;
    align-items: center;
    gap: 9px;
    margin: 0 0 12px;
    color: var(--group-tone);
    font-size: ${({ theme }) => theme.fontSize.sm};
    font-weight: 650;
  }
  & ol {
    list-style: none;
    counter-reset: change;
    margin: 0;
    padding: 0;
    display: flex;
    flex-direction: column;
    gap: 10px;
  }
  & li {
    position: relative;
    padding-left: 29px;
    color: ${({ theme }) => theme.color.textMuted};
    font-size: ${({ theme }) => theme.fontSize.xs};
    line-height: 1.65;
  }
  & li::before {
    counter-increment: change;
    content: counter(change);
    position: absolute;
    left: 0;
    top: 2px;
    width: 19px;
    height: 19px;
    display: grid;
    place-items: center;
    border: 1px solid transparent;
    border-radius: 50%;
    background: color-mix(in srgb, var(--group-tone) 14%, transparent);
    color: var(--group-tone);
    font-size: 0.65rem;
    line-height: 1;
  }
  & summary {
    cursor: pointer;
    list-style-position: outside;
    margin-left: 12px;
  }
  & summary::marker {
    color: var(--group-tone);
    font-size: 0.7em;
  }
  & summary:focus-visible {
    outline: 2px solid ${({ theme }) => theme.color.focusRing};
    outline-offset: 2px;
  }
  & details p {
    margin: 8px 0 0;
  }
`;
const GROUPS: Record<ChangeGroup, { title: string; icon: IconName }> = {
  new: { title: 'New', icon: 'plus' },
  fixes: { title: 'Fixes', icon: 'wrench' },
  improvements: { title: 'Improvements', icon: 'trending-up' },
  issues: { title: 'Known issues', icon: 'flag' },
};

export function ReleaseCard({
  release,
  open,
  onToggle,
}: {
  release: UpdateRelease;
  open: boolean;
  onToggle: () => void;
}) {
  const id = `release-${release.version}`;
  function group(name: ChangeGroup) {
    const entries = release.entries.filter((entry) => entry.group === name);
    if (!entries.length && name !== 'issues' && name !== 'improvements') return null;
    return (
      <Group $tone={name}>
        <h3>
          <Icon name={GROUPS[name].icon} size={21} />
          {GROUPS[name].title}
        </h3>
        {entries.length ? (
          <ol>
            {entries.map((entry) => (
              <li key={entry.id}>
                {entry.summary ? (
                  <details>
                    <summary>{entry.summary}</summary>
                    <p>{entry.text}</p>
                  </details>
                ) : (
                  entry.text
                )}
              </li>
            ))}
          </ol>
        ) : (
          <Muted>
            {name === 'issues'
              ? 'No known issues are listed in these release notes.'
              : 'See the changes listed in this release.'}
          </Muted>
        )}
      </Group>
    );
  }
  return (
    <Card id={id} $open={open} aria-labelledby={`${id}-heading`}>
      <Header
        id={`${id}-heading`}
        type="button"
        aria-expanded={open}
        aria-controls={`${id}-body`}
        onClick={onToggle}
      >
        <Badge>v{release.version}</Badge>
        <Description>
          <strong>{release.title}</strong>
          <time dateTime={release.date}>Released {releaseDate(release.date)}</time>
        </Description>
        <Tags>
          {release.platforms.map((platform) => (
            <Chip key={platform}>
              <PlatformIcon platform={platform} size={15} />
              {PLATFORM_LABELS[platform]}
            </Chip>
          ))}
          {release.categories.slice(0, 2).map((category) => (
            <Chip key={category} data-category={category}>
              {CATEGORY_LABELS[category]}
            </Chip>
          ))}
        </Tags>
        <span
          style={{
            transform: open ? 'rotate(180deg)' : undefined,
            marginLeft: 'auto',
            display: 'inline-flex',
          }}
        >
          <Icon name="chevron-down" size={17} />
        </span>
      </Header>
      {open && (
        <Body id={`${id}-body`} data-testid={`release-body-${release.version}`}>
          <Muted>{release.summary}</Muted>
          <Columns>
            <div>
              {group('new')}
              {group('fixes')}
            </div>
            <div>
              {group('improvements')}
              {group('issues')}
            </div>
          </Columns>
        </Body>
      )}
    </Card>
  );
}
